import { LockOutlined, LogoutOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { logout } from '@/services/user';
import { clearAuthTokens, LOGIN_PATH } from '@/utils/authSession';
import HeaderDropdown from '../HeaderDropdown';
import ChangePasswordDrawer from './ChangePasswordDrawer';

export type GlobalHeaderRightProps = {
  children?: React.ReactNode;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    avatarName: {
      display: 'inline-block',
      maxWidth: 140,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      fontSize: 14,
      fontWeight: 400,
      fontStyle: 'Regular',
      color: '#ffffff',
      lineHeight: '100%',
      letterSpacing: '0px',
    },
  };
});

export const AvatarName = () => {
  const { styles } = useStyles();
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  // 使用备用字段显示用户名称
  const displayName =
    currentUser?.nickname || currentUser?.username || '未获取到用户信息';
  return <span className={styles.avatarName}>{displayName}</span>;
};

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  children,
}) => {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  /** 退出登录，跳转登录页（不带 redirect 等 URL 参数） */
  const loginOut = async () => {
    try {
      await logout();
    } catch {
      // 忽略退出接口失败，仍清理本地登录态
    }
    clearAuthTokens();
    history.replace(LOGIN_PATH);
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'change-password') {
      setChangePasswordOpen(true);
    }
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  // 如果用户信息不存在，显示 loading
  if (!currentUser) {
    return loading;
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <ChangePasswordDrawer
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
};
